import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

import type { FormData } from 'types/formData';
import {
  INITIAL_FORM_DATA,
  buildInitialFormData,
  loadFormDataFromStorage,
  mergeFormDataFromApiPayload,
  persistFormDataToStorage,
} from 'types/formData';

type FormSectionKey = keyof FormData;

type FormDataAction =
  | { type: 'loadApi'; payload: FormData }
  | {
      type: 'updateSection';
      section: FormSectionKey;
      data: FormData[FormSectionKey];
    }
  | { type: 'wipe' };

function formDataReducer(state: FormData, action: FormDataAction): FormData {
  switch (action.type) {
    case 'loadApi':
      return mergeFormDataFromApiPayload(action.payload);
    case 'updateSection':
      return {
        ...state,
        [action.section]: action.data,
      };
    case 'wipe':
      return buildInitialFormData();
    default:
      return state;
  }
}

function initFormState(): FormData {
  return loadFormDataFromStorage();
}

interface FormDataContextValue {
  data: FormData;
  updateSection: <K extends FormSectionKey>(
    section: K,
    data: FormData[K],
  ) => void;
  loadFromApi: (payload: FormData) => void;
  wipeData: () => void;
}

const FormDataContext = createContext<FormDataContextValue | null>(null);

export const FormDataProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [state, dispatch] = useReducer(
    formDataReducer,
    INITIAL_FORM_DATA,
    initFormState,
  );

  useEffect(() => {
    persistFormDataToStorage(state);
  }, [state]);

  const updateSection = useCallback(
    <K extends FormSectionKey>(section: K, data: FormData[K]) => {
      dispatch({ type: 'updateSection', section, data });
    },
    [],
  );

  const loadFromApi = useCallback((payload: FormData) => {
    dispatch({ type: 'loadApi', payload });
  }, []);

  const wipeData = useCallback(() => {
    dispatch({ type: 'wipe' });
  }, []);

  const value = useMemo(
    () => ({
      data: state,
      updateSection,
      loadFromApi,
      wipeData,
    }),
    [loadFromApi, state, updateSection, wipeData],
  );

  return (
    <FormDataContext.Provider value={value}>
      {children}
    </FormDataContext.Provider>
  );
};

export function useFormDataContext(): FormDataContextValue {
  const ctx = useContext(FormDataContext);
  if (!ctx) {
    throw new Error('useFormDataContext must be used within FormDataProvider');
  }
  return ctx;
}
